'use client'

import { ReactNode, useEffect, useState } from 'react'
import { Button, Cell, Dialog, Divider, Navigation, Spacer, Text } from '@vinted/web-ui'
import { X24 } from '@vinted/monochrome-icons'
import { noop } from 'lodash'

import { PepType } from '@marketplace-web/pay-identity/politically-exposed-person-data'
import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { ScrollableArea } from '@marketplace-web/common-components/scrollable-area-ui'
import { ContentLoader } from '@marketplace-web/common-components/content-loader-ui'
import { UiState } from '@marketplace-web/shared/ui-state-util'
import {
  startOnboarding,
  createPaymentsAccount,
} from '@marketplace-web/pay-identity/payments-account-data'

import {
  clickEvent,
  viewScreenEvent,
  confirmWalletEvent,
} from '@marketplace-web/balance-activation/balance-activation-data'
import { AddressModel } from '@marketplace-web/address/address-data'

import { getIsPersonalIdNumberFieldRequired, mapPepDetails } from '../BalanceActivationForm/utils'
import { useBalanceActivationState } from '../BalanceActivationForm/useBalanceActivationState'
import { FormModel } from '../BalanceActivationForm/types'

import SetupForm from './SetupForm'
import EditAddressModal from './EditAddressModal'

type Labels = {
  title: string | ReactNode
  description: string | ReactNode
}

enum FormModal {
  ActivateBalance,
  Address,
}

type Props = {
  labels: Labels
  isOpen: boolean
  onSuccess: () => void
  onCancel?: () => void
}

const BalanceActivationModal = ({ labels, isOpen, onSuccess, onCancel = noop }: Props) => {
  const translate = useTranslate('activate_balance')
  const { track } = useTracking()

  const {
    form: values,
    address,
    addressType,
    lockedFields,
    maskedFields,
    agreementHint,
    isBalanceActivated,
    bannedNationalities,
    isSsnNumberRequired,
    isNationalityRequired,
    isPepDeclarationRequired,
    status: activateBalanceStatus,
    personalIdNumberRequiredNationalities,
    setForm,
    setAddress,
    getDefaultFormValues,
  } = useBalanceActivationState()

  const { user } = useSession()

  const currentUserId = user?.id ?? null
  const [isConfigLoaded, setIsConfigLoaded] = useState(false)
  const [errors, setErrors] = useState<Array<ErrorItem>>([])
  const [isLoading, setIsLoading] = useState(false)
  const [activeModal, setActiveModal] = useState<FormModal | null>(null)

  const trackUserConfirmWallet = () => {
    track(
      confirmWalletEvent({
        source: 'transaction',
      }),
    )
  }

  useEffect(() => {
    if (!isOpen) return

    track(viewScreenEvent({ screen: 'payments_account_in_transaction' }))
    if (currentUserId) startOnboarding(currentUserId)
  }, [track, isOpen, currentUserId])

  useEffect(() => {
    if (isConfigLoaded) return

    getDefaultFormValues(currentUserId)
    setIsConfigLoaded(true)
  }, [getDefaultFormValues, currentUserId, isConfigLoaded])

  useEffect(() => {
    if (isOpen) {
      setActiveModal(FormModal.ActivateBalance)
    }
  }, [isOpen])

  const showActivateBalanceModal = () => setActiveModal(FormModal.ActivateBalance)

  const showChangeAddressModal = () => setActiveModal(FormModal.Address)

  const getDefaultValues = (): FormModel => {
    const {
      firstName,
      lastName,
      birthdate,
      ssn,
      personalIdNumber,
      nationality,
      pepType,
      pepFullName,
      pepOrganisation,
      pepPosition,
      pepPositionDate,
    } = values

    return {
      firstName,
      lastName,
      birthdate,
      ssn,
      personalIdNumber,
      address,
      nationality,
      pepType: pepType || (isPepDeclarationRequired ? PepType.NotPep : undefined),
      pepFullName,
      pepOrganisation,
      pepPosition,
      pepPositionDate,
    }
  }

  const getCreatePaymentsAccountArgs = (form: FormModel) => {
    if (!currentUserId || !form.address?.id) return null

    const isPersonalIdNumberFieldRequired = getIsPersonalIdNumberFieldRequired(
      personalIdNumberRequiredNationalities,
      form.nationality,
    )

    const personalIdNumber = isPersonalIdNumberFieldRequired ? form.personalIdNumber : undefined

    return {
      firstName: form.firstName,
      lastName: form.lastName,
      userId: currentUserId,
      birthdate: form.birthdate,
      ssnNumber: form.ssn,
      personalIdNumber,
      addressId: form.address.id,
      nationality: form.nationality,
      pepDetails: mapPepDetails(form),
    }
  }

  const handleAddressClick = (form: FormModel) => {
    setForm(form)

    showChangeAddressModal()
  }

  const handleFormSubmit = async (form: FormModel) => {
    const args = getCreatePaymentsAccountArgs(form)

    if (!args) return

    setIsLoading(true)

    const response = await createPaymentsAccount(args)

    if ('errors' in response) {
      setErrors(response.errors)
      setIsLoading(false)

      return
    }

    track(
      clickEvent({
        target: 'submit',
        screen: 'payments_account_in_transaction',
        targetDetails: 'success',
      }),
    )

    setIsLoading(false)

    trackUserConfirmWallet()

    if (onSuccess) onSuccess()
  }

  const handleAddressChanged = (newAddress: AddressModel) => {
    setAddress(newAddress)
    showActivateBalanceModal()
  }

  const renderDescription = () => {
    if (!labels) return null

    return (
      <Cell styling="wide">
        <Text as="h1" type="heading" text={labels.title} />
        <Spacer />
        <Text as="span" type="body" text={labels.description} />
      </Cell>
    )
  }

  const renderContent = () => {
    const isStateLoading =
      activateBalanceStatus !== UiState.Success && activateBalanceStatus !== UiState.Failure

    if (isStateLoading)
      return (
        <ContentLoader
          styling={ContentLoader.Styling.Wide}
          testId="balance-activation-modal-loader"
        />
      )

    return (
      <>
        {renderDescription()}
        <SetupForm
          defaultValues={getDefaultValues()}
          isSsnNumberRequired={isSsnNumberRequired}
          isNationalityRequired={isNationalityRequired}
          isPepDeclarationRequired={!!isPepDeclarationRequired}
          personalIdNumberRequiredNationalities={personalIdNumberRequiredNationalities}
          bannedNationalities={bannedNationalities}
          isBalanceActivated={isBalanceActivated}
          agreementHint={agreementHint}
          lockedFields={lockedFields}
          maskedFields={maskedFields}
          apiErrors={errors}
          isLoading={isLoading}
          address={address}
          addressType={addressType}
          setAddress={setAddress}
          onAddressClick={handleAddressClick}
          onSubmit={handleFormSubmit}
          onCancel={onCancel}
        />
      </>
    )
  }

  const renderCloseButton = () => {
    return (
      <Button
        aria={{
          'aria-label': translate('a11y.actions.close'),
        }}
        styling="flat"
        theme="amplified"
        iconName={X24}
        inline
        onClick={onCancel}
        testId="balance-activation-modal-close-button"
      />
    )
  }

  const renderEditAddressModal = () => {
    return (
      <EditAddressModal
        address={address}
        isOpen={activeModal === FormModal.Address}
        onChange={handleAddressChanged}
        onCancel={showActivateBalanceModal}
      />
    )
  }

  const renderBalanceActivationModal = () => {
    return (
      <Dialog
        show={activeModal === FormModal.ActivateBalance}
        hasScrollableContent
        testId="balance-activation-modal"
      >
        <div className="u-fill-width">
          <Navigation
            right={renderCloseButton()}
            body={
              <Text
                as="h2"
                type="title"
                width="parent"
                alignment="center"
                text={translate('header')}
              />
            }
          />
          <Divider />
          <ScrollableArea>{renderContent()}</ScrollableArea>
        </div>
      </Dialog>
    )
  }

  if (!isOpen) return null

  return (
    <>
      {renderBalanceActivationModal()}
      {renderEditAddressModal()}
    </>
  )
}

export default BalanceActivationModal
